import { useEffect, useMemo, useRef, useState } from 'react';
import { Editor, useAttachPicker, useEditorInsert } from '../editor/Editor.tsx';
import { useApp } from '../app/store.ts';
import { store } from '../data/db.ts';
import { backlinksOf } from '../data/links.ts';
import { deriveTitle } from '../data/markdown.ts';
import { captureVersion } from '../data/versions.ts';
import { buildVaultZip, downloadZip } from '../lib/export.ts';
import { History } from './History.tsx';
import { revokeShare, shareNote, shareUrl, storedShare, type StoredShare } from '../data/shares.ts';
import { Icon } from './Icon.tsx';
import { Dictation, dictationAvailable } from '../todos/dictate.ts';
import type { LocalNote } from '../data/types.ts';

type Props = { note: LocalNote; onOpenPalette: () => void };

/**
 * Kopfzeile nach docs/DESIGN.md: links der Ordner mit seinem Farbquadrat, rechts
 * „Verlauf" als beschriftetes Ziel, dahinter Anpinnen und ··· für alles Seltene.
 * Zwei benannte Aktionen, zwei Symbole — statt vier gleichwertiger Piktogramme.
 */
export function NoteView({ note, onOpenPalette }: Props): JSX.Element {
  const { notes, folders, restoreContent, togglePin, trash, untrash, moveToFolder, markVersion, open } =
    useApp();
  const [showHistory, setShowHistory] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [share, setShare] = useState<StoredShare | null>(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expiry, setExpiry] = useState<number | null>(30);
  const menuRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const insert = useEditorInsert();
  const attach = useAttachPicker();
  const [canDictate, setCanDictate] = useState(false);
  const [recording, setRecording] = useState(false);
  const [dictating, setDictating] = useState(false);
  const dictation = useRef(new Dictation());
  const backlinks = useMemo(() => backlinksOf(note.id), [note.id, notes]);
  const folder = folders.find((entry) => entry.id === note.folderId);

  useEffect(() => {
    void dictationAvailable().then(setCanDictate);
    const current = dictation.current;
    return () => current.cancel();
  }, []);

  useEffect(() => {
    if (!showShare) return;
    void storedShare(note.id).then(setShare);
  }, [showShare, note.id]);

  useEffect(() => {
    if (!showMenu) return undefined;
    const close = (event: MouseEvent): void => {
      const target = event.target as Node;
      // Den eigenen Knopf ausnehmen: Sonst schließt dieser Griff das Menü beim
      // Drücken, und der Klick danach öffnet es sofort wieder — es ließe sich nur
      // durch Danebentippen loswerden.
      if (menuRef.current?.contains(target) || moreRef.current?.contains(target)) return;
      setShowMenu(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showMenu]);

  async function toggleMic(): Promise<void> {
    if (recording) {
      setRecording(false);
      setDictating(true);
      try {
        const text = await dictation.current.stop();
        // Kein Deuten im Editor: gesprochener Text ist hier einfach Text.
        if (text) insert(text);
      } finally {
        setDictating(false);
      }
      return;
    }
    try {
      await dictation.current.start();
      setRecording(true);
    } catch {
      setRecording(false);
    }
  }

  async function restore(content: string): Promise<void> {
    const current = await store.getNote(note.id);
    if (current) await captureVersion(current);
    await restoreContent(note.id, content);
  }

  return (
    <section className="note">
      <header className="note-head">
        <button className="head-icon back" onClick={() => open(null)} title="Zurück">
          <Icon name="back" />
        </button>

        <label className="folder-pick">
          <span className={`folder-square folder-square-${folder?.color ?? 0}`} />
          <select
            value={note.folderId ?? ''}
            onChange={(event) => void moveToFolder(note.id, event.target.value || null)}
          >
            <option value="">Kein Ordner</option>
            {folders.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>

        <span className="spacer" />

        {note.deletedAt === null ? (
          <>
            {canDictate && (
              <button
                className={recording ? 'head-icon mic-on' : 'head-icon'}
                title={recording ? 'Aufnahme beenden' : 'Diktieren'}
                onClick={() => void toggleMic()}
                disabled={dictating}
              >
                <Icon
                  name={dictating ? 'wait' : recording ? 'stop' : 'mic'}
                  filled={recording}
                />
              </button>
            )}
            <button
              className="head-act head-wide"
              title="Teilen"
              onClick={() => {
                setShowShare((value) => !value);
                setShowMenu(false);
              }}
            >
              Teilen
            </button>
            <button className="head-act head-wide" title="Verlauf" onClick={() => setShowHistory(true)}>
              <Icon name="history" />
              Verlauf
            </button>
            <button
              className={note.pinned ? 'head-icon on' : 'head-icon'}
              title={note.pinned ? 'Nicht mehr anpinnen' : 'Anpinnen'}
              onClick={() => void togglePin(note.id)}
            >
              <Icon name="pin" filled={note.pinned} />
            </button>
            <button
              ref={moreRef}
              className="head-icon"
              title="Mehr"
              onClick={() => setShowMenu((value) => !value)}
            >
              <Icon name="more" />
            </button>
          </>
        ) : (
          <button className="head-act" onClick={() => void untrash(note.id)}>
            <Icon name="undo" />
            Wiederherstellen
          </button>
        )}

        {showShare && (
          <div className="share-panel">
            <h3>Diese Notiz teilen</h3>
            {share ? (
              <>
                <div className="share-url">
                  <input readOnly value={shareUrl(share)} onFocus={(e) => e.target.select()} />
                  <button
                    className="ghost"
                    onClick={() => {
                      void navigator.clipboard.writeText(shareUrl(share));
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2500);
                    }}
                  >
                    {copied ? 'Kopiert' : 'Kopieren'}
                  </button>
                </div>
                <p className="hint">
                  Der Schlüssel steht hinter dem #. Er erreicht den Server nie — wer den
                  Link hat, kann lesen, sonst niemand.
                  {share.expiresAt !== null &&
                    ` Läuft am ${new Date(share.expiresAt).toLocaleDateString('de-DE')} ab.`}
                </p>
                <div className="share-actions">
                  <button
                    className="act"
                    disabled={shareBusy}
                    onClick={() => {
                      setShareBusy(true);
                      void shareNote(note, expiry)
                        .then(() => storedShare(note.id).then(setShare))
                        .finally(() => setShareBusy(false));
                    }}
                  >
                    {shareBusy ? 'wird aktualisiert …' : 'Link aktualisieren'}
                  </button>
                  <span className="spacer" />
                  <button
                    className="act"
                    onClick={() => {
                      void revokeShare(note.id, share.id).then(() => setShare(null));
                    }}
                  >
                    Widerrufen
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="hint">
                  Es entsteht ein eingefrorener Stand, verschlüsselt mit einem eigenen
                  Schlüssel. secret-Blöcke werden dabei grundsätzlich entfernt.
                </p>
                <div className="share-actions">
                  <select
                    value={expiry === null ? 'nie' : String(expiry)}
                    onChange={(event) =>
                      setExpiry(event.target.value === 'nie' ? null : Number(event.target.value))
                    }
                  >
                    <option value="7">Läuft nach 7 Tagen ab</option>
                    <option value="30">Läuft nach 30 Tagen ab</option>
                    <option value="nie">Kein Ablauf</option>
                  </select>
                  <span className="spacer" />
                  <button
                    className="solid"
                    disabled={shareBusy}
                    onClick={() => {
                      setShareBusy(true);
                      void shareNote(note, expiry)
                        .then(() => storedShare(note.id).then(setShare))
                        .finally(() => setShareBusy(false));
                    }}
                  >
                    {shareBusy ? 'wird erzeugt …' : 'Link erzeugen'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {showMenu && (
          <div className="menu" ref={menuRef}>
            {/* Auf dem Telefon ist im Kopf kein Platz für sechs Schaltflächen; dort
                stehen diese beiden hier. */}
            <button
              className="menu-narrow"
              onClick={() => {
                setShowMenu(false);
                setShowShare(true);
              }}
            >
              Teilen
            </button>
            <button
              className="menu-narrow"
              onClick={() => {
                setShowMenu(false);
                setShowHistory(true);
              }}
            >
              Verlauf
            </button>
            <button
              title="Version markieren"
              onClick={() => {
                setShowMenu(false);
                void markVersion(note.id);
              }}
            >
              Version markieren
            </button>
            <button
              onClick={() => {
                setShowMenu(false);
                attach();
              }}
            >
              Datei anhängen
            </button>
            <button
              onClick={() => {
                setShowMenu(false);
                void buildVaultZip([note], folders).then((zip) =>
                  downloadZip(zip, `${deriveTitle(note.content)}.zip`),
                );
              }}
            >
              Als Markdown exportieren
            </button>
            <button
              onClick={() => {
                setShowMenu(false);
                onOpenPalette();
              }}
            >
              Alle Befehle <em>⌘K</em>
            </button>
            <hr />
            <button
              onClick={() => {
                setShowMenu(false);
                void trash(note.id);
              }}
            >
              In den Papierkorb
            </button>
            <p className="menu-foot">
              Zuletzt bearbeitet{' '}
              {new Date(note.updatedAt).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        )}
      </header>

      <Editor noteId={note.id} />

      {backlinks.length > 0 && (
        <div className="backlinks">
          <span className="label">Erwähnt in</span>
          {backlinks.map((entry) => (
            <button key={entry.id} onClick={() => open(entry.id)}>
              {deriveTitle(entry.content)}
            </button>
          ))}
        </div>
      )}

      {/* Vier Knöpfe à 44 px — was fehlt, holt die Palette. */}
      <div className="keybar">
        <button onClick={() => insert('- [ ] ')}>Aufgabe</button>
        <button onClick={() => insert('**')}>Fett</button>
        <button onClick={() => insert('[[]]')}>Link</button>
        <button onClick={attach}>Bild</button>
        <button onClick={onOpenPalette}>⌘</button>
      </div>

      {showHistory && (
        <History
          note={note}
          onClose={() => setShowHistory(false)}
          onRestore={(content) => void restore(content)}
        />
      )}
    </section>
  );
}
